import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { SheetModeControls } from "@/components/r3f/controls/sheet-mode-controls";
import { DesaturationSheetsPipeline } from "@/components/r3f/renderers/desaturation-pipeline";
import { OdometryPathsRenderer } from "@/components/r3f/renderers/odometry-paths/odometry-paths-renderer";
import { SheetRenderer } from "@/components/r3f/renderers/sheet-renderer";
import {
  centerOrthoCamera,
  useCenterCameraOnPlaceholders,
  useOrthoCameraRotationFromSheet,
} from "@/hooks/use-center-camera-on-placeholders";
import { useIElementClippingBoxObb } from "@/hooks/use-object-bounding-box";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { useCurrentScene } from "@/modes/mode-data-context";
import { SheetWaypoints } from "@/modes/sheet-mode/sheet-waypoints";
import { useWaypointsTextures } from "@/modes/sheet-mode/use-waypoints-textures";
import { WaypointPreview } from "@/modes/sheet-mode/waypoint-preview";
import { useCached3DObjectsIfExists } from "@/object-cache";
import { selectActiveArea } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { CameraViewType } from "@/utils/cam-view";
import {
  MapWaypointsRendererRef,
  selectMainAreaVolume,
  useNonExhaustiveEffect,
  useReproportionCamera,
} from "@faro-lotv/app-component-toolbox";
import { assert, GUID } from "@faro-lotv/foundation";
import { type IElementGenericOdometryPath } from "@faro-lotv/ielement-types";
import { FloorPlanBackgroundTransparency } from "@faro-lotv/lotv";
import { ThreeEvent } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { OrthographicCamera } from "three";
import { ScansExportFallbackMarkers } from "./scans-export-fallback-markers";
import { useScansExportStore } from "./scans-export-store";
import { usePrepareScansExportData } from "./use-prepare-scans-export-data";

/** @returns A scene to be rendered when the scans export option is selected */
export function ScansExportScene(): JSX.Element {
  const { activeSheets } = useCurrentScene();
  const sheetObjects = useCached3DObjectsIfExists(
    activeSheets,
    "ScansExportScene",
  );

  const {
    paths,
    panos,
    scans,
    scanToPanoPathMap,
    markerScans,
    markerScanToTreeScanMap,
  } = usePrepareScansExportData();

  const activeArea = useAppSelector(selectActiveArea);
  assert(activeArea, "An active area must be selected in scans export mode");

  const { waypointTextures, flashWaypointTextures } = useWaypointsTextures();
  const areaVolume = useAppSelector(selectMainAreaVolume(activeArea));
  const areaBox = useIElementClippingBoxObb(areaVolume);

  const cameraRotation = useOrthoCameraRotationFromSheet(
    activeSheets.at(0)?.id,
  );
  const cameraData = useCenterCameraOnPlaceholders({
    areaVolume: areaBox,
    sheetElements: activeSheets,
    cameraRotation,
    placeholders: [],
  });

  // Create a new orthographic camera for the scans export scene
  const { activeCamera, setCameraProjection, cameraProjection } =
    useActiveCamera();

  // Store the initial camera projection to restore it later
  const [initialCameraProjection] = useState(cameraProjection);
  useEffect(() => {
    if (initialCameraProjection !== CameraViewType.orthographic) {
      setCameraProjection(CameraViewType.orthographic);
      return () => {
        setCameraProjection(initialCameraProjection);
      };
    }
  }, [initialCameraProjection, setCameraProjection]);

  // Center and reproportion the camera
  useReproportionCamera(activeCamera);
  useNonExhaustiveEffect(() => {
    if (activeCamera instanceof OrthographicCamera) {
      centerOrthoCamera(activeCamera, cameraData);
    }
  }, [activeArea.id, activeCamera]);
  const {
    hoveredIds,
    selectedIds,
    selectId,
    hoverId,
    toggleSelectedId,
    clearHovering,
  } = useScansExportStore();

  // Create the index mapping for panos and paths for placeholder hover/selection handling
  const panosToIndex = useMemo(
    () => new Map(panos.map((el, index) => [el.id, index])),
    [panos],
  );

  const [mapPlaceholders, setMapPlaceholders] =
    useState<MapWaypointsRendererRef | null>(null);

  useScansExportWaypointsSelected(
    mapPlaceholders,
    selectedIds,
    panosToIndex,
    scanToPanoPathMap.forward,
  );
  useScansExportWaypointsHovered(
    mapPlaceholders,
    hoveredIds,
    panosToIndex,
    scanToPanoPathMap.forward,
  );

  const handleElementSelection = useCallback(
    (
      elementId: GUID,
      event: { ctrlKey?: boolean; shiftKey?: boolean; metaKey?: boolean },
      selectedIds: GUID[],
    ) => {
      // Resolve the element ID to a scan ID: either via the pano/path reverse map,
      // or directly if the element itself is a scan (e.g. fallback markers)
      const scanId =
        scanToPanoPathMap.reverse.get(elementId) ??
        (scans.has(elementId) ? elementId : undefined);
      if (!scanId) return;

      if (
        selectedIds.includes(scanId) ||
        event.ctrlKey ||
        event.shiftKey ||
        event.metaKey
      ) {
        toggleSelectedId(scanId);
      } else {
        selectId(scanId);
      }
    },
    [scanToPanoPathMap.reverse, scans, selectId, toggleSelectedId],
  );

  const handlePathToSectionMapping = useCallback(
    (event: ThreeEvent<MouseEvent>, path: IElementGenericOdometryPath) =>
      handleElementSelection(path.id, event.nativeEvent, selectedIds),
    [handleElementSelection, selectedIds],
  );

  const handlePanoToSectionMapping = useCallback(
    (waypoint: IElementWaypoint, event: MouseEvent) =>
      handleElementSelection(waypoint.id, event, selectedIds),
    [handleElementSelection, selectedIds],
  );

  const [previewElement, setPreviewElement] = useState<IElementWaypoint>();
  const updateWaypointHoverState = useCallback(
    (waypoint?: IElementWaypoint) => {
      if (!waypoint) {
        clearHovering();
        setPreviewElement(undefined);
        return;
      }
      setPreviewElement(waypoint);

      const scanIndex = scanToPanoPathMap.reverse.get(waypoint.id);
      if (!scanIndex) return;

      const scan = scans.get(scanIndex);
      if (!scan) return;

      hoverId(scan.id);
    },
    [scanToPanoPathMap.reverse, scans, hoverId, clearHovering],
  );

  const handleFallbackMarkerClicked = useCallback(
    (scanId: GUID, event: MouseEvent) =>
      handleElementSelection(scanId, event, selectedIds),
    [handleElementSelection, selectedIds],
  );

  const handleMarkerHovered = useCallback(
    (scanId: GUID | undefined) => {
      if (scanId === undefined) {
        clearHovering();
      } else {
        hoverId(scanId);
      }
    },
    [hoverId, clearHovering],
  );

  return (
    <>
      {sheetObjects.map((sheet, index) => (
        <SheetRenderer
          sheet={sheet}
          backgroundTransparent={FloorPlanBackgroundTransparency.Alpha}
          key={sheet.iElement.id}
          index={index}
        />
      ))}
      <SheetModeControls camera={activeCamera} referencePlaneHeight={0} />
      <OdometryPathsRenderer
        paths={paths}
        onPlaceholderHovered={updateWaypointHoverState}
        onPathClick={handlePathToSectionMapping}
        activePathIds={selectedIds}
      />
      <SheetWaypoints
        paths={[]}
        placeholders={panos}
        ref={setMapPlaceholders}
        onPlaceholderClicked={handlePanoToSectionMapping}
        onPlaceholderHovered={updateWaypointHoverState}
        textures={waypointTextures}
        flashTextures={flashWaypointTextures}
      />
      <ScansExportFallbackMarkers
        scans={markerScans}
        markerScanToTreeScanMap={markerScanToTreeScanMap}
        selectedIds={selectedIds}
        hoveredIds={hoveredIds}
        onMarkerClicked={handleFallbackMarkerClicked}
        onMarkerHovered={handleMarkerHovered}
      />
      <DesaturationSheetsPipeline enabled={false} transparentBackground />
      {previewElement && <WaypointPreview placeholder={previewElement} />}
    </>
  );
}

function useScansExportWaypointsSelected(
  mapPlaceholders: MapWaypointsRendererRef | null | undefined,
  selectedIds: GUID[],
  panoToSectionIndex: Map<GUID, number>,
  scanToPanoPathMap: Map<GUID, GUID>,
): void {
  useEffect(() => {
    if (!mapPlaceholders) return;
    const selectedScansIndices: number[] = [];
    for (const id of selectedIds) {
      const panoId = scanToPanoPathMap.get(id);
      if (!panoId) continue;

      const panoIndex = panoToSectionIndex.get(panoId);
      if (panoIndex !== undefined && panoIndex >= 0) {
        selectedScansIndices.push(panoIndex);
      }
    }

    mapPlaceholders.selectedIds = selectedScansIndices;
  }, [mapPlaceholders, panoToSectionIndex, scanToPanoPathMap, selectedIds]);
}

function useScansExportWaypointsHovered(
  mapPlaceholders: MapWaypointsRendererRef | null | undefined,
  hoveredIds: GUID[],
  panoToSectionIndex: Map<GUID, number>,
  scanToPanoPathMap: Map<GUID, GUID>,
): void {
  useEffect(() => {
    if (!mapPlaceholders) return;
    const hoveredScansIndices: number[] = [];
    for (const id of hoveredIds) {
      const panoId = scanToPanoPathMap.get(id);
      if (!panoId) continue;
      const panoIndex = panoToSectionIndex.get(panoId);
      if (panoIndex !== undefined && panoIndex >= 0) {
        hoveredScansIndices.push(panoIndex);
      }
    }

    mapPlaceholders.hoveredIds = hoveredScansIndices;
  }, [mapPlaceholders, panoToSectionIndex, hoveredIds, scanToPanoPathMap]);
}
